import * as Immutable from "immutable";
import { defaultCompare } from "logic/DefaultCompare";
import Capability from "./Capability";
import Grantee from "./Grantee";
import ActiveShare from "./ActiveShare";
import SharedEntity from "./SharedEntity";
import SelectedGrantee from "./SelectedGrantee";
import ValidationResult from "./ValidationResult";
const _missingDependenciesFromJSON = (missingDependenciesJSON) => {
  let missingDependencies = Immutable.Map();
  Object.keys(missingDependenciesJSON).forEach((granteeGRN) => {
    const dependencyList = missingDependenciesJSON[granteeGRN];
    missingDependencies = missingDependencies.set(
      granteeGRN,
      dependencyList.map((dependency) => SharedEntity.fromJSON(dependency))
    );
  });
  return missingDependencies;
};
const _sortAndOrderGrantees = (grantees, activeShares) => {
  const granteesByType = grantees.filter(
    (grantee) => !activeShares || activeShares.findIndex((activeShare) => activeShare.grantee === grantee.id) >= 0
  ).sort((granteeA, granteeB) => defaultCompare(granteeA.title, granteeB.title)).groupBy((grantee) => grantee.type);
  const newGrantees = grantees.filter(
    (grantee) => activeShares && activeShares.findIndex((activeShare) => activeShare.grantee === grantee.id) === -1
  ).reverse();
  return Immutable.List().concat(
    newGrantees,
    granteesByType.get("error"),
    granteesByType.get("global"),
    granteesByType.get("team"),
    granteesByType.get("user")
  ).filter((grantee) => !!grantee).toList();
};
export default class EntityShareState {
  constructor(entity, availableGrantees, availableCapabilities, activeShares, selectedGranteeCapabilities, missingDependencies, validationResults) {
    this._value = {
      entity,
      availableGrantees: _sortAndOrderGrantees(availableGrantees),
      availableCapabilities,
      activeShares,
      selectedGranteeCapabilities,
      missingDependencies,
      validationResults
    };
  }
  get entity() {
    return this._value.entity;
  }
  get availableGrantees() {
    return this._value.availableGrantees;
  }
  get availableCapabilities() {
    return this._value.availableCapabilities;
  }
  get activeShares() {
    return this._value.activeShares;
  }
  get selectedGranteeCapabilities() {
    return this._value.selectedGranteeCapabilities;
  }
  get missingDependencies() {
    return this._value.missingDependencies;
  }
  get validationResults() {
    return this._value.validationResults;
  }
  get selectedGrantees() {
    const _userLookup = (userId) => this._value.availableGrantees.find((grantee) => grantee.id === userId);
    const granteesWithCapabilities = this._value.selectedGranteeCapabilities.entrySeq().map(([granteeId, roleId]) => {
      const grantee = _userLookup(granteeId);
      if (!grantee) {
        return SelectedGrantee.create(granteeId, `not found ${granteeId} (error)`, "error", roleId);
      }
      return SelectedGrantee.create(grantee.id, grantee.title, grantee.type, roleId);
    }).toList();
    return _sortAndOrderGrantees(granteesWithCapabilities, this._value.activeShares);
  }
  toBuilder() {
    const {
      entity,
      availableGrantees,
      availableCapabilities,
      activeShares,
      selectedGranteeCapabilities,
      missingDependencies,
      validationResults
    } = this._value;
    return new Builder(
      Immutable.Map({
        entity,
        availableGrantees,
        availableCapabilities,
        activeShares,
        selectedGranteeCapabilities,
        missingDependencies,
        validationResults
      })
    );
  }
  toJSON() {
    const {
      entity,
      availableGrantees = Immutable.List(),
      availableCapabilities = Immutable.List(),
      activeShares = Immutable.List(),
      selectedGranteeCapabilities = Immutable.Map(),
      missingDependencies = Immutable.Map(),
      validationResults
    } = this._value;
    return {
      entity,
      available_grantees: availableGrantees.toJS(),
      available_capabilities: availableCapabilities.toJS(),
      active_shares: activeShares.toJS(),
      selected_grantee_capabilities: selectedGranteeCapabilities.toJS(),
      missing_permissions_on_dependencies: missingDependencies.toJS(),
      validation_result: validationResults
    };
  }
  static fromJSON(value) {
    const {
      entity,
      available_grantees,
      available_capabilities,
      active_shares,
      selected_grantee_capabilities,
      missing_permissions_on_dependencies,
      validation_result
    } = value;
    const availableGrantees = Immutable.fromJS(available_grantees.map((ag) => Grantee.fromJSON(ag)));
    const availableCapabilities = Immutable.fromJS(available_capabilities.map((ar) => Capability.fromJSON(ar)));
    const activeShares = Immutable.fromJS(active_shares.map((as) => ActiveShare.fromJSON(as)));
    const selectedGranteeCapabilities = Immutable.fromJS(selected_grantee_capabilities);
    const missingDependencies = _missingDependenciesFromJSON(missing_permissions_on_dependencies);
    const validationResults = ValidationResult.fromJSON(validation_result);
    return new EntityShareState(
      entity,
      availableGrantees,
      availableCapabilities,
      activeShares,
      selectedGranteeCapabilities,
      missingDependencies,
      validationResults
    );
  }
  static builder() {
    return new Builder();
  }
}
class Builder {
  constructor(value = Immutable.Map()) {
    this.value = value;
  }
  entity(value) {
    return new Builder(this.value.set("entity", value));
  }
  availableGrantees(value) {
    return new Builder(this.value.set("availableGrantees", value));
  }
  availableCapabilities(value) {
    return new Builder(this.value.set("availableCapabilities", value));
  }
  activeShares(value) {
    return new Builder(this.value.set("activeShares", value));
  }
  selectedGranteeCapabilities(value) {
    return new Builder(this.value.set("selectedGranteeCapabilities", value));
  }
  missingDependencies(value) {
    return new Builder(this.value.set("missingDependencies", value));
  }
  validationResults(value) {
    return new Builder(this.value.set("validationResults", value));
  }
  build() {
    const {
      entity,
      availableGrantees,
      availableCapabilities,
      activeShares,
      selectedGranteeCapabilities,
      missingDependencies,
      validationResults
    } = this.value.toObject();
    return new EntityShareState(
      entity,
      availableGrantees,
      availableCapabilities,
      activeShares,
      selectedGranteeCapabilities,
      missingDependencies,
      validationResults
    );
  }
}
